import * as Dfns from 'date-fns/fp';
import {
  BlanketTrafficInstruction,
  BlanketTrafficIsci
} from '@blisspointmedia/bpm-types/dist/LinearBlanketTraffic';
import { isCreativeLive } from '@blisspointmedia/bpm-types/dist/linearBuyingUtils';
import { AVAILS, LENGTHS, PlanRow } from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { CreativeMap } from '@src/redux/creative';
import { DATE_FORMAT, snapToMonday } from '@src/utils/date-utils';
import { CANCELED_ORDER_STATUSES } from '@src/utils/order-status-utils';
import { ORDER_TYPE_OPTIONS } from './linear-buying-constants';
import { getRotationTotal } from './linear-buying-utils';
import {
  AVAIL_OPTIONS,
  BlanketCombo,
  BlanketRotationRow,
  BlanketValidity,
  CoverageRow,
  DraftDetails,
  ICON_NETWORK_PREFIX,
  InstructionScope,
  OPEN_ENDED,
  ScopeRow
} from './blanket-traffic-constants';

/** Blanket instructions are offered only on ICON networks. */
export const isIconNetwork = (network: string): boolean => network.startsWith(ICON_NETWORK_PREFIX);

/**
 * The end of an instruction's live-check range. `end_week` is the Monday of the last week the
 * instruction covers, and that whole week is covered -- so the range has to run to the following
 * Monday, the same +7 every other `isCreativeLive` call site applies. Passing the raw Monday would
 * drop a creative whose live window opens partway through that final week, even though the
 * instruction genuinely covers it.
 */
const liveRangeEnd = (endWeek: string | null): string =>
  endWeek ? Dfns.format(DATE_FORMAT, Dfns.addDays(7, Dfns.parseISO(endWeek))) : OPEN_ENDED;

export const comboKey = ({ network, avail, type }: BlanketCombo): string =>
  `${network}_${avail}_${type}`;

export const sameCombo = (a: BlanketCombo, b: BlanketCombo): boolean =>
  a.network === b.network && a.avail === b.avail && a.type === b.type;

/**
 * Every network/avail/buy-type an instruction can be written for: the distinct ICON combinations
 * present in the buy for the weeks on screen. Buy type is part of the identity — an instruction
 * never spans types, so a network bought both Regular and Secured carries one instruction each.
 */
export const getBlanketCombos = (planRows: PlanRow[]): BlanketCombo[] => {
  const byKey = new Map<string, BlanketCombo>();
  for (const { network, avail, type } of planRows) {
    if (!isIconNetwork(network)) {
      continue;
    }
    const combo = { network, avail, type };
    byKey.set(comboKey(combo), combo);
  }
  return [...byKey.values()].sort(
    (a, b) =>
      a.network.localeCompare(b.network) || a.avail.localeCompare(b.avail) || a.type - b.type
  );
};

/**
 * A withdrawn instruction is history: it covers nothing, and the editor starts fresh rather than
 * revising it.
 */
export const isInstructionWithdrawn = (instruction: { status: number }): boolean =>
  CANCELED_ORDER_STATUSES.has(instruction.status);

/**
 * Whether an instruction is actually in force. A draft is still editable and still listed, but it
 * covers nothing — the server applies the same rule, and if the two disagreed the grid would stop
 * warning about rows that in fact still need traffic.
 */
export const isInstructionLive = (instruction: { pending: boolean; status: number }): boolean =>
  !instruction.pending && !isInstructionWithdrawn(instruction);

/**
 * Whether an instruction's scope reaches a row at all: same network/avail/buy-type, the row's
 * week inside the window, and the row's market inside the instruction's (a null market on the
 * instruction means every market). Says nothing about whether the instruction is in force or
 * whether its rotation carries the row's spot length.
 *
 * Shared so the grid's coverage check and the editor's "which instruction do I open" lookup
 * cannot drift: they want different liveness rules but the same notion of scope.
 */
export const scopeCoversRow = (instruction: InstructionScope, row: ScopeRow): boolean =>
  sameCombo(instruction, row) &&
  row.week >= instruction.start_week &&
  (instruction.end_week === null || row.week <= instruction.end_week) &&
  ((instruction.market ?? null) === null || instruction.market === (row.market ?? null));

/**
 * Whether an instruction's rotation approves anything for a spot length. An ISCI with a null
 * length applies to every length on the buy; otherwise the length has to be named outright.
 */
export const rotationCoversLength = (
  iscis: BlanketTrafficIsci[],
  length: (typeof LENGTHS)[number]
): boolean => iscis.some(isci => isci.length === null || isci.length === length);

/**
 * The live instruction covering a buy row, if any: in scope, sent, and carrying a rotation for
 * the row's spot length.
 *
 * The length check is what stops a partly-filled instruction from blanking out rows it does not
 * actually cover. The editor lets a rotation be saved for only some of a buy's lengths, so a
 * :15-only instruction is a normal thing to have -- and without this, every :30 row in the same
 * scope would read as covered, losing both its "missing rotation" warning and its per-line
 * editor, leaving no way to get a creative onto it.
 */
export const findCoveringInstruction = <
  T extends InstructionScope & {
    pending: boolean;
    status: number;
    iscis: BlanketTrafficIsci[];
  }
>(
  instructions: T[],
  row: CoverageRow
): T | undefined =>
  instructions.find(
    instruction =>
      isInstructionLive(instruction) &&
      scopeCoversRow(instruction, row) &&
      rotationCoversLength(instruction.iscis, row.length)
  );

/**
 * The spot lengths a blanket instruction has to cover: the distinct lengths actually ordered on
 * that network/avail/buy-type, plus any the saved rotation already carries. The editor renders a
 * rotation section per length rather than asking the user to pick one, because a network's buy
 * routinely mixes :15s and :30s and each length traffics as its own rotation.
 *
 * Deliberately not filtered by the instruction's week window. The window governs when the
 * instruction applies, not which lengths the buy contains, and filtering by it would leave an
 * instruction starting beyond the loaded weeks with no sections at all.
 *
 * `savedRotation` is why this takes more than the plan rows. Those rows are only the weeks the
 * date picker has loaded, so a length ordered solely outside that range contributes no section --
 * and because saving replaces the whole rotation, a length with no section is a length deleted
 * from the instruction the network receives. Seeding from the saved rotation as well means every
 * length already in the pool is rendered, and so survives the save. A length ordered outside the
 * visible weeks that has no saved rotation yet still gets no section: nothing is lost, but it has
 * to be set up from a view that includes its weeks.
 */
export const getBlanketLengths = (
  planRows: PlanRow[],
  combo: BlanketCombo,
  savedRotation: BlanketTrafficIsci[] = []
): (typeof LENGTHS)[number][] => {
  const lengths = new Set<(typeof LENGTHS)[number]>();
  for (const row of planRows) {
    if (sameCombo(row, combo)) {
      lengths.add(row.length);
    }
  }
  for (const { length } of savedRotation) {
    // A null length applies to every length rather than naming one, so it adds no section of its
    // own -- it seeds the sections the buy and the rest of the rotation already establish.
    if (length !== null) {
      lengths.add(length);
    }
  }
  return [...lengths].sort((a, b) => a - b);
};

/**
 * The creatives offered for one length section of a blanket instruction. Unlike
 * `getCreativeOptions`, which gates on a single plan row's week, a blanket spans a week *range* —
 * so a creative qualifies if it's live during any part of the window.
 *
 * A creative already in the saved rotation is always offered even if it has since gone non-live,
 * so it can be removed; `notLive` marks it for the UI.
 */
export const getBlanketCreativeOptions = (
  creativeMap: CreativeMap,
  colorMap: Record<string, string>,
  {
    avail,
    startWeek,
    endWeek,
    length,
    currentRotation
  }: {
    avail: (typeof AVAILS)[number];
    startWeek: string;
    endWeek: string | null;
    length: (typeof LENGTHS)[number];
    currentRotation: BlanketTrafficIsci[];
  }
): BlanketRotationRow[] => {
  if (!creativeMap) return [];

  // An ISCI can reach this section twice -- once scoped to this length, once applying to every
  // length. The section shows one row per creative, so the specific entry wins; keying on the
  // ISCI alone would let whichever came last silently overwrite the other's percent.
  const savedByIsci = new Map<string, BlanketTrafficIsci>();
  for (const entry of currentRotation) {
    const existing = savedByIsci.get(entry.isci);
    if (!existing || (existing.length === null && entry.length !== null)) {
      savedByIsci.set(entry.isci, entry);
    }
  }

  const rangeEnd = liveRangeEnd(endWeek);
  const options: BlanketRotationRow[] = [];
  for (const creative of Object.values(creativeMap)) {
    const { isci } = creative;
    const saved = savedByIsci.get(isci);
    const live = isCreativeLive(creative, startWeek, rangeEnd);
    const matchesScope = creative.avail === avail && creative.length === length;

    if (!(live && matchesScope) && !saved) continue;

    options.push({
      isci,
      creative: creative.name,
      percent: saved?.percent ?? 0,
      color: colorMap[isci] || 'white',
      length,
      // A creative already stored as every-length stays every-length unless the user gives it a
      // different share in different sections; one not yet in the rotation is scoped to the
      // section it was added from.
      savedLength: saved ? saved.length : length,
      notLive: !(live && matchesScope)
    });
  }

  return options.sort((a, b) => a.isci.localeCompare(b.isci));
};

/**
 * A blanket rotation still has to total 100%, but deliberately has no cap on how many creatives
 * share it. A per-line rotation caps creatives at the row's spot count, which forces a single
 * ISCI onto a one-spot make-good; a blanket pool is what the network may choose from, so the
 * count does not apply.
 */
export const getBlanketValidity = (rotation: BlanketRotationRow[]): BlanketValidity => {
  const allocated = rotation.filter(({ percent }) => percent > 0);
  const { isValidTotal, offBy } = getRotationTotal(allocated);
  return {
    hasAllocations: allocated.length > 0,
    isValidTotal,
    // Unlike a per-line week, an empty blanket rotation is not "untouched" -- it is an
    // instruction that approves nothing, so there is nothing to send.
    isValid: allocated.length > 0 && isValidTotal,
    offBy
  };
};

/**
 * The whole rotation in the shape the save endpoint takes, dropping creatives zeroed out on their
 * way off. Takes every section at once rather than one at a time, because an every-length ISCI is
 * rendered in all of them and has to be reconciled across them before it can be written.
 *
 * An ISCI stored as every-length (`length: null`) is seeded into each section, so saving section
 * by section would write it once per length and quietly demote it to a set of concrete-length
 * entries -- it would stop applying to a length added to the buy later. It keeps its null length
 * here as long as the user left it at one share everywhere. Giving it different shares in
 * different sections is a decision that it is no longer one every-length entry, so it splits into
 * per-length entries at the shares the user set.
 */
export const toSaveParams = (
  sections: { rotation: BlanketRotationRow[] }[]
): BlanketTrafficIsci[] => {
  const allocated = sections.flatMap(({ rotation }) =>
    rotation.filter(({ percent }) => percent > 0)
  );

  const everyLength = allocated.filter(({ savedLength }) => savedLength === null);
  const byIsci = new Map<string, BlanketRotationRow[]>();
  for (const row of everyLength) {
    byIsci.set(row.isci, [...(byIsci.get(row.isci) ?? []), row]);
  }

  const params: BlanketTrafficIsci[] = [];
  for (const { isci, percent, length, savedLength } of allocated) {
    if (savedLength !== null) {
      params.push({ isci, percent, length });
      continue;
    }
    const shares = byIsci.get(isci) ?? [];
    const sameEverywhere = shares.every(share => share.percent === percent);
    if (!sameEverywhere) {
      params.push({ isci, percent, length });
    } else if (!params.some(param => param.isci === isci && param.length === null)) {
      params.push({ isci, percent, length: null });
    }
  }
  return params;
};

/**
 * The rows a blanket instruction does not cover, i.e. the ones that still need a rotation of their
 * own.
 *
 * Coverage is a per-week question. An instruction's window can open or close partway through the
 * weeks on screen, so a multi-week rotation is routinely part covered and part not, and treating
 * it as all-or-nothing in either direction is wrong: refuse to edit any week and a genuinely
 * uncovered one can never be trafficked; allow every week and the covered ones get a per-line
 * rotation the network is never told about.
 *
 * `isBlanketCovered` is undefined when the feature is off, which leaves every row editable.
 */
export const getUncoveredRows = <T>(rows: T[], isBlanketCovered?: (row: T) => boolean): T[] =>
  isBlanketCovered ? rows.filter(row => !isBlanketCovered(row)) : rows;

/**
 * Whether an instruction covers every one of these rows. Used where the caller holds a group that
 * stands for several weeks -- the buy grid's aggregated multi-week row -- and has to decide the
 * question for the group as a whole. An empty group is not covered: there is nothing to cover.
 */
export const areAllRowsCovered = <T>(rows: T[], isBlanketCovered?: (row: T) => boolean): boolean =>
  rows.length > 0 && getUncoveredRows(rows, isBlanketCovered).length === 0;

/** Pending-edit key. Length-qualified because one ISCI can appear in several length tabs. */
export const percentKey = (length: number, isci: string): string => `${length}_${isci}`;

export const comboLabel = ({ avail, type }: BlanketCombo): string => {
  const availLabel = AVAIL_OPTIONS.find(option => option.value === avail)?.label ?? avail;
  // ORDER_TYPE_OPTIONS is the page's one buy-type list; deriving a second one here would let the
  // two drift as types are added.
  const typeLabel =
    ORDER_TYPE_OPTIONS.find(option => Number(option.value) === type)?.label ?? `${type}`;
  return `${availLabel} · ${typeLabel}`;
};

/**
 * Blank stays blank — an empty end week means the instruction is open-ended.
 *
 * A half-typed date is passed through untouched rather than snapped: snapToMonday formats
 * whatever parseISO returns, and date-fns throws a RangeError on an invalid date, which takes
 * the whole page down through the error boundary. A `type="date"` input reports '' until the
 * value is complete, so nothing invalid is ever persisted.
 */
export const toMonday = (value: string): string => {
  if (!value) {
    return '';
  }
  const parsed = Dfns.parseISO(value);
  return Number.isNaN(parsed.getTime()) ? value : snapToMonday(value);
};

export const emptyDetails = (defaultWeek: string): DraftDetails => ({
  startWeek: defaultWeek,
  endWeek: '',
  notes: ''
});

export const detailsFromInstruction = (instruction: BlanketTrafficInstruction): DraftDetails => ({
  startWeek: instruction.start_week,
  endWeek: instruction.end_week ?? '',
  notes: instruction.notes ?? ''
});

/**
 * The instruction a combination's editor should open: one whose window covers the weeks on
 * screen. An instruction whose window has closed is deliberately not returned -- editing it would
 * bump the revision of a document the network already finished with, leaving the current weeks
 * uncovered. The editor starts a new instruction for those weeks instead.
 *
 * Withdrawn instructions are skipped for the same reason: saving sends the opened instruction's
 * id, so returning a cancelled one would revise the cancelled row rather than start fresh. This
 * is deliberately not the full `isInstructionLive` check -- a pending draft has to stay editable,
 * that being the point of a draft.
 */
export const findInstruction = (
  instructions: BlanketTrafficInstruction[],
  combo: BlanketCombo,
  week: string
): BlanketTrafficInstruction | undefined =>
  instructions.find(
    instruction =>
      !isInstructionWithdrawn(instruction) && scopeCoversRow(instruction, { ...combo, week })
  );
